import { useCallback, useEffect } from "react";
import { SUPERADMIN_USER_ID } from "constants/constants";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useUser } from "./context/UserContext";
import { useURLContext } from "./context/URLContext";
import UserLogin from "./pages/user_component/UserLogin";
import Configuration from "./pages/configuration/ConfigurationPage";
import Layout from "./components/layout/Layout";
import SwaggerPage from "./pages/SwaggerPage";
import EvaEntityDetailPage from "./pages/eva/EvaEntityDetailPage";
import EvaToolDetailPage from "./pages/eva/EvaToolDetailPage";
import DarwinEvaListPage from "./pages/eva/EvaListPage";
import EvaEntityCreatePage from "./pages/eva/EvaEntityCreatePage";
import SessionViewManager from "pages/chat/SessionViewManager";
import NewChatPage from "pages/chat/NewChatPage";
import AnalystListPage from "pages/analystList/AnalystListPage";
import AnalystPage from "pages/analyst/AnalystPage";
import ChatSession from "pages/chat/ChatSession";
import WorkflowPage from "pages/workflow/WorkflowPage";
import { AnalystChatContextProvider } from "context/AnalystChatContext";
import { UIContextProvider } from "context/UIContext";
import { ChatContextProvider } from "context/ChatContext";
import DataConfiguratorContainer from "containers/data_query/DataConfiguratorContainer";
import { init, AuthType } from "@thoughtspot/visual-embed-sdk";
import UserSettingPage from "./pages/user_component/UserSettingPage";
import "katex/dist/katex.min.css";
import ProtectedEvaRoute from "pages/eva/ProtectedEvaRoute";
import WorkflowDetailPage from "pages/workflow/WorkflowDetailPage";

function App() {
  // Access isLoggedIn and reload function from the user context
  const {
    userId,
    isLoggedIn,
    tsLoginUsername,
    tsLoginPassword,
    tsLoginUrl,
    tsTokenUrl,
    tsTokenValiditySecs,
    tsBaseUrl,
    isLoading,
    setIsTSInitialized,
  } = useUser();
  const { setPreLoginUrl } = useURLContext();
  const location = useLocation();

  // Authenticates with ThoughtSpot and initialises the embed SDK.
  // Extracted from the useEffect below so the logic is named and readable independently.
  // Only runs for SUPERADMIN_USER_ID — regular users don't have ThoughtSpot access.
  const loginToThoughtSpot = useCallback(async () => {
    // Only proceed if user is logged in and we have ThoughtSpot credentials
    if (!isLoggedIn || !tsLoginUsername || !tsLoginPassword || isLoading) {
      return;
    }
    setIsTSInitialized(false);

    try {
      if (tsTokenUrl) {
        // Token-based embed flow:
        // 1. Newton sends us the ThoughtSpot token URL and validity duration on login.
        // 2. We call init() here — this only stores the config, no network requests are made yet.
        // 3. The SDK calls getAuthToken the first time an embed component (LiveBoard/Answer) renders:
        //    a. It first checks if a ThoughtSpot session cookie already exists (GET to ThoughtSpot).
        //    b. If no session: our getAuthToken fires — we POST credentials to tsTokenUrl and get
        //       a bearer token back. The SDK then exchanges that token for a session cookie.
        //    c. If a session already exists: the cookie is reused and getAuthToken is NOT called.
        // 4. All subsequent embed components in the same tab reuse the session cookie silently.
        // 5. getAuthToken is called again only when ThoughtSpot's session cookie expires or a 401
        //    is returned — which with a 30-day token will almost never happen in normal usage.
        init({
          thoughtSpotHost: tsBaseUrl,
          authType: AuthType.TrustedAuthToken,
          getAuthToken: async () => {
            const tokenResponse = await fetch(tsTokenUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                username: tsLoginUsername,
                password: tsLoginPassword,
                validity_time_in_sec: tsTokenValiditySecs, // controlled via application.properties
              }),
            });
            if (!tokenResponse.ok) {
              throw new Error("ThoughtSpot token fetch failed: " + tokenResponse.status);
            }
            const tokenData = await tokenResponse.json();
            return tokenData.token; // SDK uses this as the Bearer token for all embed requests
          },
        });
      } else {
        // Cookie mode (fallback): POST credentials to obtain a session cookie, then init with None.
        const response = await fetch(tsLoginUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-By": "XMLHttpRequest",
          },
          credentials: "include",
          body: JSON.stringify({
            username: tsLoginUsername,
            password: tsLoginPassword,
            rememberMe: true,
          }),
        });

        if (!response.ok) {
          console.error("ThoughtSpot login failed: " + response.status + " " + response.statusText);
          return;
        }

        init({
          thoughtSpotHost: tsBaseUrl,
          authType: AuthType.None,
        });
      }

      setIsTSInitialized(true);
    } catch (err) {
      console.error("ThoughtSpot login error: " + err);
      setIsTSInitialized(false);
    }
  }, [
    isLoggedIn,
    tsLoginUsername,
    tsLoginPassword,
    isLoading,
    tsLoginUrl,
    tsTokenUrl,
    tsTokenValiditySecs,
    tsBaseUrl,
    setIsTSInitialized,
  ]);

  // Trigger ThoughtSpot login when auth state or credentials change
  useEffect(() => {
    if (userId === SUPERADMIN_USER_ID) {
      loginToThoughtSpot();
    }
  }, [loginToThoughtSpot, userId]);

  // Store the current URL when user is not logged in (except for root path)
  useEffect(() => {
    if (!isLoggedIn) {
      const fullPath = location.pathname + location.search;
      // Only store if it's not the root path to avoid unnecessary redirects
      if (fullPath !== "/") {
        setPreLoginUrl(fullPath);
      }
    }
  }, [isLoggedIn, location.pathname, location.search, setPreLoginUrl]);

  const authenticatedRoutes = (
    // UIContext and AnalystChatContext are app-wide: every route including Navbar can read them.
    // ChatContextProvider is also app-level — SessionViewManager wraps ALL routes and keeps
    // background ChatPage instances mounted, so they need ChatContext available at all times.
    <UIContextProvider>
      <AnalystChatContextProvider>
        <ChatContextProvider>
          <Layout>
            <Routes>
              {/* SessionViewManager wraps ALL routes as a persistent layout component.
                  It stays mounted for any URL — background chat sessions survive navigation
                  to /analyst, /workflows, and every other page. Streams are never interrupted.
                  Active route content renders via <Outlet /> inside SessionViewManager. */}
              <Route element={<SessionViewManager />}>
                <Route path="/" element={<Navigate to="/chat/new" replace />} />
                <Route path="/chat/new" element={<NewChatPage />} />
                {/* No UI here — SessionViewManager's active ChatPage renders the chat */}
                <Route path="/chat/:id" element={null} />
                <Route
                  path="/eva/agents"
                  element={
                    <ProtectedEvaRoute>
                      <DarwinEvaListPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/tools"
                  element={
                    <ProtectedEvaRoute>
                      <DarwinEvaListPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/playground"
                  element={
                    <ProtectedEvaRoute>
                      <DarwinEvaListPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/experiments"
                  element={
                    <ProtectedEvaRoute>
                      <DarwinEvaListPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/chat/:id"
                  element={
                    <ProtectedEvaRoute>
                      <DarwinEvaListPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/skills"
                  element={
                    <ProtectedEvaRoute>
                      <DarwinEvaListPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/users"
                  element={
                    <ProtectedEvaRoute>
                      <DarwinEvaListPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/usage-metrics"
                  element={
                    <ProtectedEvaRoute>
                      <DarwinEvaListPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/report-template"
                  element={
                    <ProtectedEvaRoute>
                      <DarwinEvaListPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva"
                  element={
                    <ProtectedEvaRoute>
                      <Navigate to="/eva/agents" replace />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/agent/create"
                  element={
                    <ProtectedEvaRoute>
                      <EvaEntityCreatePage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/skill/create"
                  element={
                    <ProtectedEvaRoute>
                      <EvaEntityCreatePage isSkillMode={true} />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/skill/:skill_id"
                  element={
                    <ProtectedEvaRoute>
                      <EvaEntityDetailPage isSkillMode={true} />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/:agent_id"
                  element={
                    <ProtectedEvaRoute>
                      <EvaEntityDetailPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route
                  path="/eva/tool/:tool_id"
                  element={
                    <ProtectedEvaRoute>
                      <EvaToolDetailPage />
                    </ProtectedEvaRoute>
                  }
                />
                <Route path="/user" element={<UserSettingPage />} />
                <Route path="/mkg/*" element={<Configuration />} />
                <Route path="/chat-session" element={<ChatSession />} />
                <Route path="/workflows" element={<WorkflowPage />} />
                <Route path="/workflows/:id" element={<WorkflowDetailPage />} />
                <Route path="/workflows/:id/definition" element={<WorkflowDetailPage />} />
                <Route path="/workflows/:id/runs" element={<WorkflowDetailPage />} />
                <Route path="/analyst" element={<AnalystListPage />} />
                <Route path="/analyst/new" element={<AnalystPage />} />
                <Route path="/analyst/:id" element={<AnalystPage />} />
                <Route path="/analyst/:id/memory" element={<AnalystPage />} />
                <Route path="/data-query/:id?" element={<DataConfiguratorContainer />} />
                <Route path="/docs/swagger" element={<SwaggerPage />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Route>
            </Routes>
          </Layout>
        </ChatContextProvider>
      </AnalystChatContextProvider>
    </UIContextProvider>
  );

  const unauthenticatedRoutes = (
    <Routes>
      <Route path="/*" element={<UserLogin />} />{" "}
    </Routes>
  );

  return <div className="App">{isLoggedIn ? authenticatedRoutes : unauthenticatedRoutes}</div>;
}

export default App;
